export type QuestionKind =
  | "text"
  | "paragraph"
  | "yesno"
  | "radio"
  | "checkboxes"
  | "dropdown";

export type FormQuestion = {
  id: string;
  label: string;
  labelEs: string;
  kind: QuestionKind;
  required: boolean;
  options: string[];
  optionsEs: string[];
};

export const QUESTION_KINDS: Array<{ value: QuestionKind; label: string }> = [
  { value: "text", label: "Short text" },
  { value: "paragraph", label: "Paragraph" },
  { value: "yesno", label: "Yes / no" },
  { value: "radio", label: "Multiple choice" },
  { value: "checkboxes", label: "Checkboxes" },
  { value: "dropdown", label: "Dropdown" },
];

const OPTION_KINDS: QuestionKind[] = ["radio", "checkboxes", "dropdown"];

export function needsOptions(kind: QuestionKind): boolean {
  return OPTION_KINDS.includes(kind);
}

export function newQuestionId(): string {
  return `q-${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultOptions(kind: QuestionKind): string[] {
  if (!needsOptions(kind)) return [];
  return ["Option 1", "Option 2"];
}

export function newQuestion(): FormQuestion {
  return {
    id: newQuestionId(),
    label: "",
    labelEs: "",
    kind: "text",
    required: false,
    options: [],
    optionsEs: [],
  };
}

export function normalizeQuestion(item: Partial<FormQuestion> & {
  id: string;
  label: string;
}): FormQuestion {
  const kind = isQuestionKind(item.kind) ? item.kind : "text";
  const rawOptions = Array.isArray(item.options)
    ? item.options.map((row) => String(row))
    : defaultOptions(kind);
  const options = needsOptions(kind)
    ? rawOptions.length > 0
      ? rawOptions
      : defaultOptions(kind)
    : [];
  return {
    id: item.id || newQuestionId(),
    label: item.label ?? "",
    labelEs: item.labelEs ?? "",
    kind,
    required: Boolean(item.required),
    options,
    optionsEs: padOptions(
      Array.isArray(item.optionsEs)
        ? item.optionsEs.map((row) => String(row))
        : [],
      options.length,
    ),
  };
}

function padOptions(values: string[], length: number): string[] {
  if (length <= 0) return [];
  const next = values.slice(0, length);
  while (next.length < length) next.push("");
  return next;
}

export function questionLabel(
  question: Pick<FormQuestion, "label" | "labelEs">,
  locale: "en" | "es",
): string {
  if (locale === "es" && question.labelEs.trim()) {
    return question.labelEs.trim();
  }
  return question.label;
}

export function questionOptions(
  question: Pick<FormQuestion, "options" | "optionsEs">,
  locale: "en" | "es",
): string[] {
  const english = question.options.map((item) => item.trim()).filter(Boolean);
  if (locale !== "es") return english;
  return english.map((option, index) => {
    const translated = question.optionsEs?.[index]?.trim();
    return translated || option;
  });
}

export function displayAnswer(
  question: FormQuestion,
  value: string | undefined,
  locale: "en" | "es",
): string {
  const raw = value?.trim() ?? "";
  if (!raw) return "-";
  if (question.kind === "yesno") {
    if (raw === "yes") return locale === "es" ? "Sí" : "Yes";
    if (raw === "no") return locale === "es" ? "No" : "No";
  }
  if (question.kind === "checkboxes") {
    return splitCheckboxAnswers(raw)
      .map((item) => displayChoice(question, item, locale))
      .join(" · ");
  }
  return displayChoice(question, raw, locale);
}

function displayChoice(
  question: Pick<FormQuestion, "options" | "optionsEs">,
  value: string,
  locale: "en" | "es",
): string {
  if (locale !== "es") return value;
  const index = question.options.findIndex((option) => option === value);
  if (index < 0) return value;
  return question.optionsEs[index]?.trim() || value;
}

export function mergeQuestionTranslation(
  current: FormQuestion,
  seed?: FormQuestion,
): FormQuestion {
  if (!seed) return current;
  return {
    ...current,
    labelEs: current.labelEs.trim() ? current.labelEs : seed.labelEs,
    optionsEs: current.optionsEs.some((item) => item.trim())
      ? padOptions(current.optionsEs, current.options.length)
      : padOptions(seed.optionsEs, current.options.length),
  };
}

function isQuestionKind(value: unknown): value is QuestionKind {
  return QUESTION_KINDS.some((item) => item.value === value);
}

export function cloneQuestions(questions: FormQuestion[]): FormQuestion[] {
  return questions.map((item) => ({
    ...item,
    id: newQuestionId(),
    options: [...item.options],
    optionsEs: [...item.optionsEs],
  }));
}

export function splitCheckboxAnswers(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function joinCheckboxAnswers(values: string[]): string {
  return values.join(" | ");
}

export function questionAnswered(
  question: FormQuestion,
  value: string | undefined,
): boolean {
  if (question.kind === "checkboxes") {
    return splitCheckboxAnswers(value).length > 0;
  }
  return Boolean(value?.trim());
}

export function missingRequiredQuestion(
  questions: FormQuestion[],
  answers: Record<string, string>,
): FormQuestion | undefined {
  return questions.find(
    (item) => item.required && !questionAnswered(item, answers[item.id]),
  );
}

export function questionDesignError(
  questions: FormQuestion[],
): string | null {
  if (questions.some((item) => !item.label.trim())) {
    return "Every extra question needs a label, or remove it.";
  }
  const incomplete = questions.find(
    (item) =>
      needsOptions(item.kind) &&
      item.options.filter((option) => option.trim()).length < 2,
  );
  if (incomplete) {
    return `Add at least two choices for: ${incomplete.label}`;
  }
  return null;
}
